import { notifyBugsnag } from '@salescore/client-base'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  createDimensionFieldName,
  type NodePath,
  type ViewQueryRecordNode,
} from '@salescore/core'
import { parseJsonIfValid } from '@salescore/frontend-common'
import { Array, pipe, Record } from 'effect'
import { isNotNullish } from 'effect/Predicate'
import { type SetterOrUpdater, useSetRecoilState } from 'recoil'
import { z } from 'zod'

import type { RSheetColumn, RSheetRecordNode } from '../../domain/column'
import { useGetCursorSelector, useGetCursorSnapshot } from '../../recoil/cursor'
import { drillDownModalAtom, type DrillDownParameterForKpiPivot } from '../../recoil/navigation/atoms'
import { useRecordsImpl } from '../../recoil/records'
import { pickedIdsAtom } from '../../recoil/records/atoms'

interface RowChange {
  row: ViewQueryRecordNode | undefined
  innerRowIndex: number
  column: RSheetColumn<RSheetRecordNode>
  value: unknown
}

export const useChangeSystemField = (
  fixedLeftColumnIndex: number,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  streamName: string | undefined,
  visibleRowNumber: number | undefined,
  // eslint-disable-next-line @typescript-eslint/max-params
) => {
  const records = useRecordsImpl()
  const getCursorSnapshot = useGetCursorSnapshot()
  const getCursorSelector = useGetCursorSelector(fixedLeftColumnIndex, columns, compiledConfig, visibleRowNumber)
  const setDrillDownModalAtom = useSetRecoilState(drillDownModalAtom)
  const setPickedIdsAtom = useSetRecoilState(pickedIdsAtom)

  return ({ row, column, innerRowIndex, value }: RowChange) => {
    const cursorModel = getCursorSnapshot()
    const { expandArea } = getCursorSelector(cursorModel)
    switch (column.type) {
      case 'open': {
        // 何かあった場合はモーダルを開く
        if (streamName === undefined) {
          notifyBugsnag({ error: new Error(`streamが見つかりません`) })
          return
        }

        setDrillDownModalAtom((x) => ({
          ...x,
          content: {
            type: 'form' as const,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            recordId: value as string,
          },
          visible: true,
        }))
        return
      }
      case 'kpi': {
        const dimensionValues = getDimensionValues(row, column.node.path, innerRowIndex, column.field?.name)
        setDrillDownModalAtom((x) => ({
          ...x,
          content: {
            type: 'kpi' as const,
            dimensionValues,
          },
          visible: true,
        }))
        return
      }
      case 'rowCheckbox': {
        if (expandArea === undefined) {
          pickIdsToggleRecord(setPickedIdsAtom, row, value)
          return
        }

        const areaRecords = (
          expandArea.area.upRowIndex <= expandArea.area.downRowIndex
            ? Array.range(expandArea.area.upRowIndex, expandArea.area.downRowIndex)
            : Array.empty<number>()
        )
          .map((index) => records[index])
          .filter(isNotNullish)

        const pickedRecord = areaRecords.find((areaRecord) => areaRecord.id === row?.id)
        if (pickedRecord === undefined) {
          pickIdsToggleRecord(setPickedIdsAtom, row, value)
          return
        }

        pickIdsOfExpandAreaRecords(setPickedIdsAtom, areaRecords)
        break
      }
      default: {
        break
      }
    }
  }
}

function pickIdsToggleRecord(
  setPickedIdsAtom: SetterOrUpdater<string[]>,
  record?: ViewQueryRecordNode,
  value?: unknown,
) {
  if (record?.id === undefined) {
    return
  }

  const togglePicked = (id: string, forcePick?: boolean) => {
    setPickedIdsAtom((oldPickedIds) => {
      const filteredIds = oldPickedIds.filter((oldId) => oldId !== id)
      const picked = [...filteredIds, id]
      const unpicked = filteredIds
      if (forcePick !== undefined) {
        return forcePick ? picked : unpicked
      }

      if (oldPickedIds.includes(id)) {
        return unpicked
      }
      return picked
    })
  }

  if (typeof value === 'boolean') {
    togglePicked(record.id, value)
  } else if (typeof value === 'string') {
    const flag = value === 'true'
    togglePicked(record.id, flag)
  } else {
    togglePicked(record.id)
  }
}

function pickIdsOfExpandAreaRecords(setPickedIdsAtom: SetterOrUpdater<string[]>, records: ViewQueryRecordNode[]): void {
  const firstRecordId = records[0]?.id
  if (firstRecordId === undefined) {
    return
  }

  setPickedIdsAtom((xs) => {
    // 先頭が含まれていれば、全て除去する
    if (xs.includes(firstRecordId)) {
      return Array.difference(xs, records.map((x) => x.id).filter(isNotNullish))
    }

    return pipe([...xs, ...records.map((x) => x.id)], Array.filter(isNotNullish), Array.dedupe)
  })
}

function getDimensionValues(
  record: RSheetRecordNode | undefined,
  nodePath: NodePath,
  innerRowIndex: number,
  fieldName: string | undefined,
): DrillDownParameterForKpiPivot['dimensionValues'] {
  if (record === undefined || fieldName === undefined) {
    return {
      rows: [],
      columns: [],
    }
  }

  const rows = getDimensionRecordNodesRec(record, nodePath.slice(1), innerRowIndex, 1)
  // eslint-disable-next-line sonarjs/todo-tag
  const columnDimensionValuesRaw = parseJsonIfValid(fieldName) // TODO: ここのzip,unzipのロジックの統一
  const parsedColumnDimensionValues = z.unknown().array().safeParse(columnDimensionValuesRaw)
  // eslint-disable-next-line sonarjs/todo-tag
  const columnsValues = (parsedColumnDimensionValues.success ? parsedColumnDimensionValues.data : []).slice(0, -1) // TODO: 最後にrole(actual)が入っているという暗黙知が入ってしまっている
  return {
    rows,
    // eslint-disable-next-line sonarjs/todo-tag
    columns: columnsValues.map((value) => ({ value, label: undefined })), // TODO: columnのラベルを取り出す方法がない？
  }
}

function getDimensionRecordNodesRec(
  record: RSheetRecordNode | undefined,
  nodePath: NodePath,
  innerRowIndex: number,
  depth: number,
): DrillDownParameterForKpiPivot['dimensionValues']['rows'] {
  if (record === undefined) {
    return []
  }
  if (nodePath.length === 0) {
    // XXX: ソートを行なった場合、通常のレコード構造ではなく、leaf側に全てのdimensionがあるので、これを処理する
    return pipe(
      record.attributes,
      Record.keys,
      Array.filter((x) => /^dimension\d+$/v.test(x)),
      Array.map((fieldName) => ({
        value: record.attributes[fieldName],
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        label: record.attributes[`${fieldName}_label`] as string | undefined,
      })),
    )
  }

  const childTable = record.children.find((x) => x.nodeName === nodePath[0])
  const childRecordNode = childTable?.children.find(
    (x) => x.meta.innerRowIndexStart <= innerRowIndex && innerRowIndex < x.meta.innerRowIndexEnd,
  )

  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: 命名規則
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: valuesNodeのrecordを防ぎたいが、綺麗に防げないのでcompact()することで許容している
  const value = record.attributes[createDimensionFieldName(depth)]
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const label = record.attributes[`${createDimensionFieldName(depth)}_label`] as string | undefined
  return [
    {
      value,
      label,
    },
    ...getDimensionRecordNodesRec(childRecordNode, nodePath.slice(1), innerRowIndex, depth + 1),
  ]
}
